import type {
  ImportMapping,
  ParsedImport,
  PickerEntry,
  SourceKind,
  ValidatedImport,
} from "../types";

const NAME_HEADERS = new Set(["name", "participant", "item", "entry", "label"]);
const NAME_HEADER_ALIASES = new Set([
  "full name",
  "employee name",
  "employee full name",
  "participant name",
  "attendee name",
  "candidate name",
  "agent name",
  "associate name",
  "staff name",
  "member name",
  "person name",
  "contact name",
  "display name",
  "preferred name",
  "item name",
  "entry name",
]);
const NAME_CONTEXT_TOKENS = new Set([
  "employee",
  "participant",
  "attendee",
  "candidate",
  "agent",
  "associate",
  "staff",
  "member",
  "person",
  "contact",
  "display",
  "preferred",
  "full",
  "item",
  "entry",
]);
const NON_PERSON_NAME_TOKENS = new Set([
  "category",
  "column",
  "department",
  "file",
  "group",
  "list",
  "sheet",
  "status",
  "team",
  "weight",
]);
const CATEGORY_HEADERS = new Set(["category", "group", "team", "department"]);
const WEIGHT_HEADERS = new Set(["weight", "tickets", "chances"]);
const BIDI_OVERRIDES = /[\u202A-\u202E\u2066-\u2069]/u;

function normalizeHeader(value: unknown): string {
  return normalizeText(value)
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[_./\\()-]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

function nameHeaderScore(header: string): number {
  if (NAME_HEADERS.has(header)) return 120;
  if (NAME_HEADER_ALIASES.has(header)) return 110;

  const tokens = header.split(" ").filter(Boolean);
  if (!tokens.includes("name")) return 0;
  if (tokens.some((token) => NON_PERSON_NAME_TOKENS.has(token))) return 0;

  return tokens.some((token) => NAME_CONTEXT_TOKENS.has(token)) ? 100 : 70;
}

function hasUnsupportedControlCharacters(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 8 || code === 11 || code === 12 || (code >= 14 && code <= 31) || code === 127;
  });
}

export function normalizeText(value: unknown): string {
  return String(value ?? "")
    .replace(/[\r\n\t]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

export function duplicateKey(name: string, category: string | null): string {
  const normalizedName = normalizeText(name).normalize("NFKC").toLocaleLowerCase();
  const normalizedCategory = normalizeText(category ?? "").normalize("NFKC").toLocaleLowerCase();
  return `${normalizedName}\u241f${normalizedCategory}`;
}

export function suggestMapping(rows: string[][]): ImportMapping {
  const firstRow = rows[0] ?? [];
  const normalizedHeaders = firstRow.map(normalizeHeader);
  const nameHeaderScores = normalizedHeaders.map(nameHeaderScore);
  const strongestNameHeaderScore = Math.max(0, ...nameHeaderScores);
  const hasRecognizedHeader = normalizedHeaders.some(
    (header) =>
      nameHeaderScore(header) > 0 ||
      CATEGORY_HEADERS.has(header) ||
      WEIGHT_HEADERS.has(header),
  );
  const populatedColumns = firstRow
    .map((cell, index) => ({ cell: normalizeText(cell), index }))
    .filter(({ cell }) => cell.length > 0);

  const nameHeaderIndex = nameHeaderScores.findIndex(
    (score) => score === strongestNameHeaderScore && score > 0,
  );
  const categoryHeaderIndex = normalizedHeaders.findIndex((header) => CATEGORY_HEADERS.has(header));
  const weightHeaderIndex = normalizedHeaders.findIndex((header) => WEIGHT_HEADERS.has(header));

  return {
    hasHeader: hasRecognizedHeader,
    nameColumn:
      nameHeaderIndex >= 0
        ? nameHeaderIndex
        : populatedColumns.length === 1
          ? populatedColumns[0]?.index ?? 0
          : 0,
    categoryColumn: categoryHeaderIndex >= 0 ? categoryHeaderIndex : null,
    weightColumn: weightHeaderIndex >= 0 ? weightHeaderIndex : null,
  };
}

export function validateParsedImport(
  parsed: ParsedImport,
  mapping: ImportMapping,
  existingEntries: PickerEntry[] = [],
): ValidatedImport {
  const rows = mapping.hasHeader ? parsed.rows.slice(1) : parsed.rows;
  const offset = mapping.hasHeader ? 2 : 1;
  const entries: PickerEntry[] = [];
  const duplicates: PickerEntry[] = [];
  const errors: Array<{ row: number; message: string }> = [];
  const seen = new Set(existingEntries.map((entry) => duplicateKey(entry.name, entry.category)));

  for (const [index, row] of rows.entries()) {
    const sourceRow = index + offset;
    const name = normalizeText(row[mapping.nameColumn]);
    const categoryValue =
      mapping.categoryColumn === null ? "" : normalizeText(row[mapping.categoryColumn]);
    const category = categoryValue || null;
    const weightValue =
      mapping.weightColumn === null ? "" : normalizeText(row[mapping.weightColumn]);

    if (!name) {
      if (row.some((cell) => normalizeText(cell))) {
        errors.push({ row: sourceRow, message: "Name is required." });
      }
      continue;
    }
    if (name.length > 120) {
      errors.push({ row: sourceRow, message: "Name exceeds 120 characters." });
      continue;
    }
    if (category && category.length > 80) {
      errors.push({ row: sourceRow, message: "Category exceeds 80 characters." });
      continue;
    }
    if (hasUnsupportedControlCharacters(name) || BIDI_OVERRIDES.test(name)) {
      errors.push({ row: sourceRow, message: "Name contains unsupported control characters." });
      continue;
    }

    let weight = 1;
    if (weightValue) {
      if (!/^\d+$/u.test(weightValue)) {
        errors.push({ row: sourceRow, message: "Weight must be an integer from 1 to 1000." });
        continue;
      }
      weight = Number(weightValue);
      if (weight < 1 || weight > 1_000) {
        errors.push({ row: sourceRow, message: "Weight must be an integer from 1 to 1000." });
        continue;
      }
    }

    const entry: PickerEntry = {
      id: crypto.randomUUID(),
      name,
      category,
      weight,
      enabled: true,
      source: {
        kind: parsed.sourceKind as SourceKind,
        fileName: parsed.fileName,
        sheet: parsed.sheet,
        row: sourceRow,
      },
    };

    const key = duplicateKey(name, category);
    if (seen.has(key)) {
      duplicates.push(entry);
      continue;
    }
    seen.add(key);
    entries.push(entry);
  }

  const categoryCount = new Set(entries.map((entry) => entry.category).filter(Boolean)).size;
  const warnings = [...parsed.parseWarnings];
  if (categoryCount > 250) {
    warnings.push("More than 250 categories were found. Simplify the source before importing.");
  } else if (categoryCount > 50) {
    warnings.push("This list has more than 50 categories. Filters may be harder to navigate.");
  }
  if (entries.length > 2_000) {
    warnings.push("Large list detected. The draw remains fair while the stage renders a visual sample.");
  }

  return { entries, duplicates, errors, warnings };
}
